'use client';

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHooks, type TrendPreset } from '@/lib/api';
import { TrendMedia } from './TrendMedia';

export function TrendsBrowser() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['trends', 'all'],
    queryFn: () => apiHooks.getTrends(),
    staleTime: 5 * 60 * 1000,
  });

  const visibleTrends = useMemo(() => {
    if (!data) return [];
    if (!activeCategory) return data.trends;
    return data.trends.filter((trend) => trend.category === activeCategory);
  }, [data, activeCategory]);

  const availableCategories = useMemo(() => {
    if (!data) return [] as string[];
    const usedCats = new Set(
      data.trends.map((trend) => trend.category).filter((c): c is string => !!c),
    );
    return data.categories.filter((c) => usedCats.has(c));
  }, [data]);

  return (
    <section className="relative overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-5 pb-20 pt-[30px] md:px-[180px] md:pb-32 md:pt-[30px]">
        <div className="flex flex-col items-center gap-5">
          <h1 className="font-display text-4xl font-bold leading-[1.275] text-white md:text-[40px]">
            Тренды
          </h1>
        </div>

        <div className="mt-10 flex gap-2 overflow-x-auto pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <CategoryChip
            label="Все"
            active={activeCategory === null}
            onClick={() => setActiveCategory(null)}
          />
          {availableCategories.map((category) => (
            <CategoryChip
              key={category}
              label={category}
              active={activeCategory === category}
              onClick={() => setActiveCategory(category)}
            />
          ))}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-6">
          {isLoading
            ? Array.from({ length: 12 }).map((_, i) => <TrendCardSkeleton key={i} />)
            : visibleTrends.map((trend) => <TrendCard key={trend.id} trend={trend} />)}
        </div>

        {!isLoading && visibleTrends.length === 0 ? (
          <div className="mt-12 text-center text-white/60">Пока нет трендов в этой категории</div>
        ) : null}
      </div>
    </section>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-[20px] px-4 py-2 text-sm transition-colors ${
        active
          ? 'bg-[#fafafa] text-[#0f0f0f]'
          : 'bg-white/[0.09] text-[#fafafa] hover:bg-white/[0.14]'
      }`}
      style={{ letterSpacing: '-0.4px' }}
    >
      {label}
    </button>
  );
}

function TrendCard({ trend }: { trend: TrendPreset }) {
  const example = trend.examples[0];

  return (
    <Link
      href={`/trends/${trend.id}`}
      className="group relative block aspect-[252/378] overflow-hidden bg-[#1a1a1d]"
      style={{ borderRadius: '31.5px 6.3px 6.3px 31.5px' }}
    >
      <TrendMedia
        src={example?.imageUrl}
        poster={example?.thumbnailUrl}
        alt={trend.name}
        isVideo={Boolean(example?.isVideo)}
        imgClassName="transition-transform duration-500 group-hover:scale-105"
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[48%] bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
        <div className="font-display text-lg leading-tight text-white md:text-[25px]">
          {trend.name}
        </div>
      </div>
    </Link>
  );
}

function TrendCardSkeleton() {
  return (
    <div
      className="aspect-[252/378] animate-pulse bg-white/5"
      style={{ borderRadius: '31.5px 6.3px 6.3px 31.5px' }}
    />
  );
}
